import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import imageCompression from 'browser-image-compression';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import SearchableCombobox from "@/components/ui/SearchableCombobox";
import { ROLE_ORDER } from "@/lib/AuthSession";
import { Info, Camera, CheckCircle2, PenLine, Upload, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import FaceRegistrationModal from "../attendance/FaceRegistrationModal";

export default function EditMemberDialog({ open, member, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFaceModalOpen, setIsFaceModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  const uniqueDivisions = Array.from(new Set(teamMembers.map(m => m.department).filter(Boolean))).filter(d => d !== "Direktur Utama" && d !== "Developer").sort();
  const uniqueRoles = Array.from(new Set([...ROLE_ORDER, ...teamMembers.map(m => m.role)])).filter(r => r !== "owner" && r !== "developer" && r).sort();

  useEffect(() => {
    if (member) {
      setForm({ ...member });
      setIsFaceModalOpen(false);
    }
  }, [member]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name || !form.email) return;
    setLoading(true);
    await base44.entities.TeamMember.update(member.id, form);
    queryClient.invalidateQueries({ queryKey: ["team-members"] });
    setLoading(false);
    onSaved?.();
    onClose();
  };

  if (!member) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Anggota Tim</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Nama Lengkap *</Label>
                <Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Password *</Label>
                <Input value={form.password || ""} onChange={(e) => set("password", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">No. Telepon</Label>
                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Employee ID</Label>
                <Input value={form.employee_id || ""} onChange={(e) => set("employee_id", e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">NIK</Label>
                <Input value={form.nik || ""} onChange={(e) => set("nik", e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Divisi</Label>
                {(form.department === "Direktur Utama" || form.department === "Developer" || form.role === "owner" || form.role === "developer") ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 rounded-lg border border-border">
                    <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground capitalize">{form.department || "Tanpa Divisi"} — divisi tidak dapat diubah</span>
                  </div>
                ) : (
                  <SearchableCombobox
                    value={form.department || ""}
                    onValueChange={(v) => set("department", v)}
                    options={uniqueDivisions}
                    placeholder="Pilih atau ketik divisi..."
                    searchPlaceholder="Cari divisi..."
                    allowCustom={true}
                  />
                )}
              </div>
              
              {/* Jika Divisi Produksi: Tampilkan pilihan Primer vs Sekunder */}
              {(form.department === "Produksi" || form.department?.toLowerCase().includes("produksi")) && (
                <div className="space-y-1 col-span-2 bg-amber-50/70 border border-amber-200 p-2.5 rounded-xl">
                  <Label className="text-xs font-bold text-amber-900 flex items-center justify-between">
                    <span>Tipe Produksi (Primer / Sekunder):</span>
                    <span className="text-[10px] text-amber-700 font-normal">*Primer = Produksi, Sekunder = Pengemasan</span>
                  </Label>
                  <Select
                    value={form.sub_divisi || (form.position?.toLowerCase().includes('pengemasan') || form.role?.toLowerCase().includes('pengemasan') ? "sekunder" : "primer")}
                    onValueChange={(v) => {
                      set("sub_divisi", v);
                      if (v === "primer") {
                        if (!form.position || form.position.includes('Pengemasan')) set("position", "Staff Produksi (Primer)");
                        if (!form.role || form.role.includes('Pengemasan')) set("role", "Staff Produksi");
                      } else if (v === "sekunder") {
                        set("position", "Staff Pengemasan (Sekunder)");
                        set("role", "Staff Pengemasan");
                      }
                    }}
                  >
                    <SelectTrigger className="bg-white border-amber-300 text-xs h-9">
                      <SelectValue placeholder="-- Pilih Tipe Produksi --" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      <SelectItem value="primer" className="text-xs">
                        🔵 Produksi Primer (Mixing, Filling, Maserasi, Bulk)
                      </SelectItem>
                      <SelectItem value="sekunder" className="text-xs">
                        🟠 Produksi Sekunder / Pengemasan (Packing, Labeling, Dus)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Jabatan (Opsional)</Label>
                <SearchableCombobox
                  value={form.role || ""}
                  onValueChange={(v) => set("role", v)}
                  options={uniqueRoles}
                  placeholder="Pilih atau ketik jabatan..."
                  searchPlaceholder="Cari jabatan..."
                  allowCustom={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status Akun</Label>
                <Select value={form.status || "aktif"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="nonaktif">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status Karyawan</Label>
                <SearchableCombobox
                  value={form.employment_status || "Karyawan Tetap"}
                  onValueChange={(v) => set("employment_status", v)}
                  options={["Karyawan Tetap", "Karyawan Kontrak", "Karyawan Percobaan (Probation)", "Harian Lepas", "Magang (Internship)", "Freelance"]}
                  placeholder="Pilih status karyawan..."
                  searchPlaceholder="Cari status..."
                  allowCustom={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Jenis Kelamin</Label>
                <Select value={form.gender || "pria"} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="pria">Pria</SelectItem>
                    <SelectItem value="wanita">Wanita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tanggal Bergabung</Label>
                <CustomDatePicker value={form.join_date || ""} onChange={(val) => set("join_date", val)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tanggal Lahir</Label>
                <CustomDatePicker 
                  value={form.birth_date || ""} 
                  onChange={(val) => set("birth_date", val)} 
                  fromYear={1950}
                  toYear={new Date().getFullYear()}
                  align="end"
                />
              </div>
              
              <div className="space-y-1 flex flex-col justify-center gap-1 pt-4">
                <Label className="text-xs">Face ID (Absensi Wajah)</Label>
                {form.face_descriptor ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-2 rounded-md border border-emerald-100 w-fit">
                    <CheckCircle2 className="w-4 h-4" /> Wajah Terekam
                    <Button variant="link" className="text-xs h-auto p-0 ml-2 text-indigo-600" onClick={() => setIsFaceModalOpen(true)}>Ubah Wajah</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="h-8 text-xs flex gap-2 w-full border-dashed border-indigo-200 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => setIsFaceModalOpen(true)}>
                    <Camera className="w-3.5 h-3.5" /> Pindai Wajah Baru
                  </Button>
                )}
              </div>

              <div className="space-y-1 flex items-center gap-4 pt-5 col-span-2">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="require_attendance" 
                    className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
                    checked={form.require_attendance !== false} 
                    onChange={(e) => set("require_attendance", e.target.checked)} 
                  />
                  <Label htmlFor="require_attendance" className="text-xs cursor-pointer">Masuk Daftar Absensi</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="saturday_off" 
                    className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
                    checked={form.saturday_off === true} 
                    onChange={(e) => set("saturday_off", e.target.checked)} 
                  />
                  <Label htmlFor="saturday_off" className="text-xs cursor-pointer">Sabtu Libur (Tidak Wajib Absen)</Label>
                </div>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Catatan</Label>
                <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
              </div>

              {/* ASA Personality Settings - Only in Edit Mode */}
              <div className="col-span-2 pt-2 pb-1 border-t border-muted/50 mt-2">
                 <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 mb-2">
                   ASA AI Personality Data
                 </h4>
                 <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                   <div className="space-y-1 col-span-2 md:col-span-1">
                     <Label className="text-xs">Nama Panggilan ASA</Label>
                     <Input value={form.asa_nickname || ""} onChange={(e) => set("asa_nickname", e.target.value)} placeholder="Cth: Budi" className="bg-white" />
                   </div>
                   <div className="space-y-1 col-span-2">
                     <Label className="text-xs">Memori Sifat & Preferensi User (Personality)</Label>
                     <Textarea 
                       value={form.asa_personality || ""} 
                       onChange={(e) => set("asa_personality", e.target.value)} 
                       rows={2} 
                       placeholder="Cth: Suka to the point, tegas, tidak suka banyak basa basi."
                       className="bg-white text-xs"
                     />
                   </div>
                 </div>
              </div>

              {/* Tanda Tangan Digital */}
              <div className="col-span-2 pt-2 pb-1 border-t border-slate-200 mt-2">
                <h4 className="text-[11px] font-bold text-[#8C6208] uppercase tracking-wider flex items-center gap-2 mb-2">
                  <PenLine className="w-3.5 h-3.5" /> Tanda Tangan Digital (TTD)
                </h4>
                <div className="bg-[#F9F5EC] p-3.5 rounded-xl border border-[#E6D5B8] space-y-2">
                  <Label className="text-xs font-semibold text-slate-700">Upload File Gambar TTD atau Paste URL (PNG / JPG)</Label>
                  <div className="flex gap-2 items-center">
                    <Input 
                      value={form.signature_url || ""} 
                      onChange={(e) => set("signature_url", e.target.value)} 
                      placeholder="Upload file gambar TTD dari HP/PC atau masukan URL..." 
                      className="bg-white text-xs font-mono flex-1 h-9"
                    />
                    <label className="cursor-pointer bg-[#8C6208] text-white hover:bg-[#724F06] text-xs px-3.5 h-9 rounded-lg font-bold shrink-0 flex items-center gap-1.5 shadow-sm transition-all">
                      <Upload className="w-3.5 h-3.5" />
                      <span>Upload File</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              let processedFile = file;
                              if (file.size > 200 * 1024) {
                                processedFile = await imageCompression(file, {
                                  maxSizeMB: 0.2,
                                  maxWidthOrHeight: 800,
                                  useWebWorker: true
                                });
                              }
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                set("signature_url", ev.target.result);
                              };
                              reader.readAsDataURL(processedFile);
                            } catch {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                set("signature_url", ev.target.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }
                        }}
                      />
                    </label>
                  </div>

                  {form.signature_url ? (
                    <div className="p-2.5 bg-white rounded-lg border border-[#E6D5B8] flex items-center justify-between mt-2 shadow-xs">
                      <span className="text-[10px] text-slate-400 font-bold uppercase">Preview TTD Saat Ini:</span>
                      <img src={form.signature_url} alt="TTD Preview" className="h-12 object-contain max-w-[180px]" crossOrigin="anonymous" />
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-400 italic">Belum ada tanda tangan. Klik Upload File untuk memilih gambar TTD dari komputer/HP.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-between items-center pt-3 border-t border-slate-100 mt-2">
              {member.role !== 'owner' ? (
                <Button 
                  type="button"
                  variant="ghost" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs font-bold gap-1.5 h-9 px-3 rounded-xl"
                  onClick={async () => {
                    const name = form.full_name || member.full_name || 'anggota tim ini';
                    if (confirm(`Yakin ingin menghapus ${name}?\n(Data akan dipindahkan ke Keranjang Sampah)`)) {
                      try {
                        await base44.entities.TeamMember.delete(member.id);
                        toast.success(`${name} berhasil dipindahkan ke Keranjang Sampah.`);
                        queryClient.invalidateQueries({ queryKey: ["team-members"] });
                        onSaved?.();
                        onClose();
                      } catch (e) {
                        console.error(e);
                        toast.error("Gagal menghapus anggota tim.");
                      }
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4" /> Hapus Anggota
                </Button>
              ) : <div />}
              <div className="flex gap-2">
                <Button variant="outline" className="h-9 text-xs rounded-xl" onClick={onClose}>Batal</Button>
                <Button 
                  onClick={handleSave} 
                  disabled={loading || !form.full_name || !form.email}
                  className="h-9 text-xs font-bold bg-[#302621] hover:bg-slate-800 text-white rounded-xl"
                >
                  {loading ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <FaceRegistrationModal 
        isOpen={isFaceModalOpen}
        onClose={() => setIsFaceModalOpen(false)}
        onRegistered={async (descriptorString, base64Image) => {
          set("face_descriptor", descriptorString);
          if (base64Image) {
            try {
              const res = await fetch(base64Image);
              const blob = await res.blob();
              const file = new File([blob], `face_${member?.id || Date.now()}.jpg`, { type: 'image/jpeg' });
              const { file_url } = await base44.integrations.Core.UploadFile({ file });
              if (file_url) set("face_photo_url", file_url);
            } catch (e) {
              console.warn("Upload face photo error:", e);
            }
          }
          setIsFaceModalOpen(false);
        }}
      />
    </>
  );
}